import { FC, forwardRef, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import classNames from 'classnames/bind';
import { Theme } from '../../types/enums';
import { FAIcon, IconStyle, Shade } from '../Icons/Icons';
import VariantSelector from '../VariantSelector/VariantSelector';
import { useTranslation } from '../Translation/TranslationProvider';
import { PillPropTypes, PillTheme } from './types/propType';
import { PillStyle, PillType } from './types/enums';
import styles from './Pill.module.scss';

const cx = classNames.bind(styles);

const Pill: FC<PillPropTypes> = forwardRef<HTMLDivElement, PillPropTypes>((props: PillPropTypes, ref) => {
  const {
    disabled = false,
    selected = false,
    title,
    imgSrc,
    colorCode,
    onPillClick,
    onClose,
    icon,
    className,
    pillStyle = PillStyle.DEFAULT,
    pillType = PillType.OUTLINE,
    theme = Theme.PURPLE,
    ariaLabel,
    ...restProps
  } = props;
  const t = useTranslation();

  const ariaLabelValue = ariaLabel || t('nextgen.IconButton.ariaLabel');

  const controlEvent = (e: ReactKeyboardEvent | ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onHandleClick = (e: ReactKeyboardEvent | ReactMouseEvent) => {
    controlEvent(e);
    if (onPillClick) {
      onPillClick(e);
    }
  };
  const onKeyEvent = (e: ReactKeyboardEvent) => {
    controlEvent(e);
    if (e.key === 'Enter') {
      onHandleClick(e);
    }
  };
  const onHandleCloseClick = (e: ReactKeyboardEvent | ReactMouseEvent) => {
    controlEvent(e);
    if (onClose) {
      onClose(e);
    }
  };

  const CClassname = cx('pill-container', className);
  const CPillClassname = cx('pill-button', { 'pill-button-solid': pillType === PillType.SOLID }, [theme]);
  const returnPill = () => {
    if (pillStyle === PillStyle.DEFAULT) {
      return (
        <button
          className={CPillClassname}
          data-testid="pill-test-id"
          disabled={disabled}
          {...restProps}
          onClick={onHandleClick}
          onKeyDown={onKeyEvent}
        >
          {icon?.iconName && (
            <FAIcon className={styles['icon']} theme={theme} {...icon} data-testid="left-pill-testid" />
          )}
          {title}
          {onClose && (
            <FAIcon
              className={`${styles['icon']} ${styles['close-icon']}`}
              iconName="circle-xmark"
              iconStyle={IconStyle.SOLID}
              shade={Shade.DARK}
              theme={theme}
              onClick={onHandleCloseClick}
              onKeyDown={(e) => e.key === 'Enter' && onHandleCloseClick(e)}
              data-testid="close-icon-testid"
            />
          )}
        </button>
      );
    } else {
      return (
        <VariantSelector
          title={title}
          disabled={disabled}
          selected={selected}
          ariaLabel={ariaLabelValue}
          onVariantSelectorClick={onHandleClick}
          colorCode={colorCode}
          imgSrc={imgSrc}
        />
      );
    }
  };

  return (
    <div className={CClassname} ref={ref}>
      {returnPill()}
    </div>
  );
});
Pill.displayName = 'Pill';
export { PillStyle, PillType };
export default Pill;
export type { PillTheme };
